import { __assign, __read } from "tslib";
import { scaleBand, scaleLinear } from 'd3-scale';
import { stack, stackOffsetDiverging } from 'd3-shape';
import { calculateRange, calculateStackedRange, calculateScaleDomain, getKeysDifference, transformToPercent, normalizeDate, getOffsetRangeColor, } from '@keen.io/charts-utils';
import { calculateGroupedBars } from './bar.utils';
export var generateHorizontalGroupedBars = function (_a) {
    var data = _a.data, keys = _a.keys, disabledKeys = _a.disabledKeys, dimension = _a.dimension, margins = _a.margins, minValue = _a.minValue, maxValue = _a.maxValue, barPadding = _a.barPadding, barsOrder = _a.barsOrder, labelSelector = _a.labelSelector, colors = _a.colors, xScaleSettings = _a.xScaleSettings, layout = _a.layout, dataSeriesOffset = _a.dataSeriesOffset;
    var filteredKeys = getKeysDifference(keys, disabledKeys);
    var precision = xScaleSettings.precision;
    var dateNormalizer = precision
        ? function (date) { return normalizeDate(date, precision); }
        : null;
    var localizedData = dateNormalizer
        ? data.map(function (item) {
            var _a;
            return (__assign(__assign({}, item), (_a = {}, _a[labelSelector] = dateNormalizer(item[labelSelector]), _a)));
        })
        : data;
    var _b = calculateRange(localizedData, minValue, maxValue, keys), minimum = _b.minimum, maximum = _b.maximum;
    var xScale = scaleLinear()
        .range([margins.left, dimension.width - margins.right])
        .domain([minimum, maximum]);
    calculateScaleDomain(xScale, minimum, maximum);
    var yScale = scaleBand()
        .range([dimension.height - margins.bottom, margins.top])
        .domain(localizedData.map(function (item) { return item[labelSelector]; }).reverse())
        .padding(barPadding);
    var yGroupScale = scaleBand()
        .range([0, yScale.bandwidth()])
        .domain(filteredKeys);
    var range = new Array(yScale.domain().length).fill(true);
    var bars = calculateGroupedBars(localizedData, yGroupScale, keys, labelSelector, disabledKeys, range, xScale, yScale, layout, colors, barsOrder, dataSeriesOffset);
    return {
        bars: bars,
        xScale: xScale,
        yScale: yScale,
        localizedData: localizedData,
    };
};
export var generateVerticalGroupedBars = function (_a) {
    var data = _a.data, dimension = _a.dimension, margins = _a.margins, minValue = _a.minValue, maxValue = _a.maxValue, barPadding = _a.barPadding, barsOrder = _a.barsOrder, keys = _a.keys, disabledKeys = _a.disabledKeys, colors = _a.colors, labelSelector = _a.labelSelector, xScaleSettings = _a.xScaleSettings, layout = _a.layout, dataSeriesOffset = _a.dataSeriesOffset;
    var filteredKeys = getKeysDifference(keys, disabledKeys);
    var precision = xScaleSettings.precision;
    var dateNormalizer = precision
        ? function (date) { return normalizeDate(date, precision); }
        : null;
    var localizedData = dateNormalizer
        ? data.map(function (item) {
            var _a;
            return (__assign(__assign({}, item), (_a = {}, _a[labelSelector] = dateNormalizer(item[labelSelector]), _a)));
        })
        : data;
    var _b = calculateRange(localizedData, minValue, maxValue, keys), minimum = _b.minimum, maximum = _b.maximum;
    var xScale = scaleBand()
        .range([margins.left, dimension.width - margins.right])
        .domain(localizedData.map(function (item) { return item[labelSelector]; }))
        .padding(barPadding);
    var yScale = scaleLinear()
        .range([dimension.height - margins.bottom, margins.top])
        .domain([minimum, maximum]);
    calculateScaleDomain(yScale, minimum, maximum);
    var xGroupScale = scaleBand()
        .range([0, xScale.bandwidth()])
        .domain(filteredKeys);
    var range = new Array(xScale.domain().length).fill(true);
    var bars = calculateGroupedBars(localizedData, xGroupScale, keys, labelSelector, disabledKeys, range, xScale, yScale, layout, colors, barsOrder, dataSeriesOffset);
    return {
        bars: bars,
        xScale: xScale,
        yScale: yScale,
        localizedData: localizedData,
    };
};
export var generateHorizontalStackedBars = function (_a) {
    var data = _a.data, dimension = _a.dimension, margins = _a.margins, minValue = _a.minValue, maxValue = _a.maxValue, barPadding = _a.barPadding, keys = _a.keys, disabledKeys = _a.disabledKeys, colors = _a.colors, stackMode = _a.stackMode, labelSelector = _a.labelSelector, xScaleSettings = _a.xScaleSettings, dataSeriesOffset = _a.dataSeriesOffset;
    var bars = [];
    var filteredKeys = getKeysDifference(keys, disabledKeys);
    var precision = xScaleSettings.precision;
    var dateNormalizer = precision
        ? function (date) { return normalizeDate(date, precision); }
        : null;
    var localizedData = dateNormalizer
        ? data.map(function (item) {
            var _a;
            return (__assign(__assign({}, item), (_a = {}, _a[labelSelector] = dateNormalizer(item[labelSelector]), _a)));
        })
        : data;
    var normalizedData = stackMode === 'normal'
        ? localizedData
        : transformToPercent(localizedData, filteredKeys);
    var stackedData = stack().keys(filteredKeys).offset(stackOffsetDiverging)(normalizedData);
    var _b = calculateStackedRange(normalizedData, minValue, maxValue, filteredKeys), minimum = _b.minimum, maximum = _b.maximum;
    var percentMin = minimum < -100 ? -100 : minimum;
    var percentMax = maximum > 100 ? 100 : maximum;
    minimum = stackMode === 'percent' ? percentMin : minimum;
    maximum = stackMode === 'percent' ? percentMax : maximum;
    var xScale = scaleLinear()
        .range([margins.left, dimension.width - margins.right])
        .domain([minimum, maximum]);
    var yScale = scaleBand()
        .range([dimension.height - margins.bottom, margins.top])
        .domain(normalizedData.map(function (item) { return item[labelSelector]; }).reverse())
        .padding(barPadding);
    calculateScaleDomain(xScale, minimum, maximum);
    var barHeight = yScale.bandwidth();
    var range = new Array(yScale.domain().length).fill(true);
    stackedData.forEach(function (item) {
        var keyName = item.key;
        range.forEach(function (_d, index) {
            var _a = __read(item[index], 2), rangeMin = _a[0], rangeMax = _a[1];
            var width = xScale(rangeMax) - xScale(rangeMin) || 0;
            var bar = {
                key: index + "." + keyName,
                selector: [index, keyName],
                x: xScale(rangeMin),
                y: yScale(normalizedData[index][labelSelector]),
                width: width,
                height: barHeight,
                color: getOffsetRangeColor(keys.indexOf(keyName), colors, dataSeriesOffset),
                colorOutOfRange: dataSeriesOffset
                    ? index < dataSeriesOffset[0] || index >= dataSeriesOffset[1]
                    : !colors[index],
                value: data[index][keyName],
            };
            bars.push(bar);
        });
    });
    return {
        bars: bars,
        xScale: xScale,
        yScale: yScale,
        localizedData: localizedData,
    };
};
export var generateVerticalStackedBars = function (_a) {
    var data = _a.data, dimension = _a.dimension, margins = _a.margins, minValue = _a.minValue, maxValue = _a.maxValue, barPadding = _a.barPadding, keys = _a.keys, disabledKeys = _a.disabledKeys, colors = _a.colors, stackMode = _a.stackMode, labelSelector = _a.labelSelector, xScaleSettings = _a.xScaleSettings, dataSeriesOffset = _a.dataSeriesOffset;
    var bars = [];
    var filteredKeys = getKeysDifference(keys, disabledKeys);
    var precision = xScaleSettings.precision;
    var dateNormalizer = precision
        ? function (date) { return normalizeDate(date, precision); }
        : null;
    var localizedData = dateNormalizer
        ? data.map(function (item) {
            var _a;
            return (__assign(__assign({}, item), (_a = {}, _a[labelSelector] = dateNormalizer(item[labelSelector]), _a)));
        })
        : data;
    var normalizedData = stackMode === 'normal'
        ? localizedData
        : transformToPercent(localizedData, filteredKeys);
    var stackedData = stack().keys(filteredKeys).offset(stackOffsetDiverging)(normalizedData);
    var _b = calculateStackedRange(normalizedData, minValue, maxValue, filteredKeys), minimum = _b.minimum, maximum = _b.maximum;
    var percentMin = minimum < -100 ? -100 : minimum;
    var percentMax = maximum > 100 ? 100 : maximum;
    minimum = stackMode === 'percent' ? percentMin : minimum;
    maximum = stackMode === 'percent' ? percentMax : maximum;
    var xScale = scaleBand()
        .range([margins.left, dimension.width - margins.right])
        .domain(normalizedData.map(function (item) { return item[labelSelector]; }))
        .padding(barPadding);
    var yScale = scaleLinear()
        .range([dimension.height - margins.bottom, margins.top])
        .domain([minimum, maximum]);
    calculateScaleDomain(yScale, minimum, maximum);
    var barWidth = xScale.bandwidth();
    var range = new Array(xScale.domain().length).fill(true);
    stackedData.forEach(function (item) {
        var keyName = item.key;
        range.forEach(function (_d, index) {
            var _a = __read(item[index], 2), rangeMin = _a[0], rangeMax = _a[1];
            var height = yScale(rangeMin) - yScale(rangeMax) || 0;
            var bar = {
                key: index + "." + keyName,
                selector: [index, keyName],
                x: xScale(normalizedData[index][labelSelector]),
                y: yScale(rangeMax),
                width: barWidth,
                height: height,
                color: getOffsetRangeColor(keys.indexOf(keyName), colors, dataSeriesOffset),
                colorOutOfRange: dataSeriesOffset
                    ? index < dataSeriesOffset[0] || index >= dataSeriesOffset[1]
                    : !colors[index],
                value: data[index][keyName],
            };
            bars.push(bar);
        });
    });
    return {
        bars: bars,
        xScale: xScale,
        yScale: yScale,
        localizedData: localizedData,
    };
};
var BAR_RENDER_MAP = {
    grouped: {
        vertical: generateVerticalGroupedBars,
        horizontal: generateHorizontalGroupedBars,
    },
    stacked: {
        vertical: generateVerticalStackedBars,
        horizontal: generateHorizontalStackedBars,
    },
};
export var generateBars = function (options) {
    var layout = options.layout, groupMode = options.groupMode, xScaleSettings = options.xScaleSettings, yScaleSettings = options.yScaleSettings, xAxisTitle = options.xAxisTitle, yAxisTitle = options.yAxisTitle;
    var settings = layout === 'vertical'
        ? {
            xScaleSettings: xScaleSettings,
            yScaleSettings: yScaleSettings,
            xAxisTitle: xAxisTitle,
            yAxisTitle: yAxisTitle,
        }
        : {
            xScaleSettings: yScaleSettings,
            yScaleSettings: xScaleSettings,
            xAxisTitle: yAxisTitle,
            yAxisTitle: xAxisTitle,
        };
    return __assign({ settings: settings }, BAR_RENDER_MAP[groupMode][layout].call(null, options));
};
